import { useEffect, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { GoogleGlyph } from '@/components/common/GoogleGlyph';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  APPLE_CUSTOMER_CLIENT_ID,
  GOOGLE_CUSTOMER_CLIENT_ID,
  MICROSOFT_CUSTOMER_CLIENT_ID,
} from '@/lib/constants';
import {
  requestEmailCode,
  signInWithApple,
  signInWithGoogle,
  signInWithMicrosoft,
  verifyEmailCode,
  type Customer,
} from '@/lib/account-api';
import { signInWithMicrosoft as msalSignIn } from '@/lib/microsoft-auth';
import {
  AppleSignInCancelledError,
  AppleSignInPopupBlockedError,
  signInWithApple as appleSignIn,
  warnIfAppleNotConfigured,
} from '@/lib/apple-auth';
import {
  trackEmailCodeEntered,
  trackEmailRequestSent,
  trackEmailVerifyFailed,
  trackLogin,
  trackSignUp,
} from '@/lib/analytics';

// Mirror of AuthContext.SESSION_HINT_KEY. Snapshot before SSO so we can tell
// first-time signup from returning login without a backend change.
const SESSION_HINT_KEY = 'cch.session';
function hadPriorSession(): boolean {
  try {
    return window.localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

// Google Identity Services — we use the OAuth 2.0 code-client only, not the
// renderButton iframe flow. The library exposes both; we just don't touch
// `google.accounts.id`. This gives us full visual control of the button:
// our own <button> element calls codeClient.requestCode(), which opens a
// popup to accounts.google.com. The popup hands a `code` back to our
// callback via postMessage. We POST the code to the Worker, which
// exchanges it server-side for an ID token.
interface GoogleCodeResponse {
  code: string;
  error?: string;
  error_description?: string;
}
interface GoogleCodeClient {
  requestCode: () => void;
}
interface GoogleOAuth2 {
  initCodeClient: (config: {
    client_id: string;
    scope: string;
    ux_mode?: 'popup' | 'redirect';
    callback: (resp: GoogleCodeResponse) => void;
  }) => GoogleCodeClient;
}
declare global {
  interface Window {
    google?: { accounts: { oauth2: GoogleOAuth2 } };
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

// Module-scoped so subsequent mounts (re-opening the modal, re-navigating to
// /account/login) reuse the already-resolved promise. Attaching fresh load
// listeners to an existing <script> whose load event has already fired would
// hang forever — the listeners never fire, so initCodeClient never runs.
let gsiPromise: Promise<void> | null = null;

function loadGsiScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = GSI_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => {
      gsiPromise = null;
      reject(new Error('GSI failed to load'));
    };
    document.head.appendChild(el);
  });
  return gsiPromise;
}

const IcMicrosoft = () => (
  <svg width={18} height={18} viewBox="0 0 24 24">
    <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
    <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
    <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
    <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
  </svg>
);

// Apple's Human Interface Guidelines constrain this mark: it must be the
// solid Apple logo, sized so its height is roughly 43% of the button height
// (18px in a 44px pill), with clear space around it, and it must be black on
// a light button. Our shared OAuth pill is white with a hairline border —
// Apple's approved "white with outline" style — so a black logo is correct.
const IcApple = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="#000" aria-hidden="true">
    <path d="M17.05 12.54c-.02-2.67 2.18-3.95 2.28-4.02-1.24-1.81-3.17-2.06-3.86-2.09-1.64-.16-3.2.97-4.03.97-.84 0-2.12-.94-3.48-.92-1.79.03-3.44 1.04-4.36 2.65-1.86 3.22-.48 7.99 1.33 10.6.89 1.28 1.94 2.71 3.33 2.66 1.33-.05 1.84-.86 3.45-.86s2.06.86 3.47.84c1.44-.03 2.34-1.3 3.22-2.59 1.02-1.48 1.44-2.91 1.46-2.99-.03-.01-2.8-1.07-2.83-4.25zM14.47 4.65c.74-.89 1.24-2.13 1.1-3.37-1.06.05-2.35.71-3.11 1.6-.68.79-1.28 2.05-1.12 3.27 1.19.09 2.4-.61 3.13-1.5z" />
  </svg>
);

interface SignInOptionsProps {
  onSuccess: (customer: Customer) => void;
  newsletterDefault?: boolean;
  /** Funnel-attribution tag persisted to customers.signup_source on first signup.
   * Ignored by the backend for returning customers. */
  signupSource?: string;
}

export default function SignInOptions({
  onSuccess,
  newsletterDefault = true,
  signupSource,
}: SignInOptionsProps) {
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [subscribeToNewsletter, setSubscribeToNewsletter] =
    useState(newsletterDefault);

  // Google OAuth code client (lazy-initialised after GSI loads).
  const [googleReady, setGoogleReady] = useState(false);
  const codeClientRef = useRef<GoogleCodeClient | null>(null);

  // Mirror state into a ref so the OAuth callback (registered once on init)
  // reads the latest checkbox value at sign-in time.
  const subscribeRef = useRef(newsletterDefault);
  useEffect(() => {
    subscribeRef.current = subscribeToNewsletter;
  }, [subscribeToNewsletter]);

  // Same reason: the Google OAuth callback closure captures props once at
  // init time, so route signupSource through a ref to survive any future
  // dynamic prop changes.
  const signupSourceRef = useRef(signupSource);
  useEffect(() => {
    signupSourceRef.current = signupSource;
  }, [signupSource]);

  // Email auth UI state. The email field is always visible (Resend-style
  // dual-path login) so users who can't or won't use OAuth see the path
  // immediately rather than hidden behind another tap. The 'code' stage
  // replaces the email input once a code's been sent.
  const [emailMode, setEmailMode] = useState<'email' | 'code'>('email');
  const [emailValue, setEmailValue] = useState('');
  const [codeValue, setCodeValue] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Surface the silently-hidden Apple button in dev. No-op in production.
  useEffect(() => {
    warnIfAppleNotConfigured();
  }, []);

  // Resend cooldown ticker — 30s after each /email/request to discourage
  // accidental double-taps and stay well inside the per-email rate limit.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = window.setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendCooldown]);

  // Load the GSI script and initialise an OAuth code client once. The
  // client is stateless; we keep the same instance for the component's
  // lifetime. requestCode() opens a fresh popup every time it's called.
  useEffect(() => {
    if (!GOOGLE_CUSTOMER_CLIENT_ID) return;
    let cancelled = false;
    loadGsiScript()
      .then(() => {
        if (cancelled || !window.google) return;
        codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
          client_id: GOOGLE_CUSTOMER_CLIENT_ID,
          // openid + email + profile gives the backend everything it needs
          // to populate the customer record (email is the identity, name
          // and picture flow through to first_name/last_name/avatar).
          scope: 'openid email profile',
          ux_mode: 'popup',
          callback: async (resp: GoogleCodeResponse) => {
            if (resp.error) {
              // user_cancelled, popup_closed_by_user, popup_blocked etc.
              // None of these warrant an error message — quietly reset.
              setBusy(false);
              return;
            }
            setBusy(true);
            setError(null);
            const returning = hadPriorSession();
            try {
              const c = await signInWithGoogle(resp.code, {
                newsletterOptIn: subscribeRef.current,
                signupSource: signupSourceRef.current,
              });
              if (returning) {
                trackLogin('google');
              } else {
                trackSignUp('google', {
                  newsletter_opt_in: subscribeRef.current,
                });
              }
              onSuccess(c);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Sign-in failed');
            } finally {
              setBusy(false);
            }
          },
        });
        setGoogleReady(true);
      })
      .catch((e: Error) => setError(e.message));

    return () => {
      cancelled = true;
    };
  }, [onSuccess]);

  const handleGoogleSignIn = () => {
    if (busy || !googleReady || !codeClientRef.current) return;
    setError(null);
    setBusy(true);
    codeClientRef.current.requestCode();
    // We don't reset busy here — the callback above handles success and
    // every error case (including popup dismissal).
  };

  const handleRequestEmailCode = async (resend = false) => {
    if (emailBusy) return;
    const email = emailValue.trim().toLowerCase();
    // Cheap client-side sanity check; the server is the source of truth.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Enter a valid email address');
      return;
    }
    setEmailError(null);
    setEmailBusy(true);
    try {
      await requestEmailCode(email, {
        newsletterOptIn: subscribeRef.current,
        signupSource,
      });
      trackEmailRequestSent();
      setEmailMode('code');
      setResendCooldown(30);
      if (resend) setCodeValue('');
    } catch (err) {
      trackEmailVerifyFailed('network');
      setEmailError(err instanceof Error ? err.message : 'Could not send code');
    } finally {
      setEmailBusy(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (emailBusy) return;
    const code = codeValue.trim();
    if (!/^\d{6}$/.test(code)) {
      setEmailError('Enter the 6-digit code from your email');
      return;
    }
    setEmailError(null);
    setEmailBusy(true);
    const returning = hadPriorSession();
    try {
      const customer = await verifyEmailCode(emailValue.trim().toLowerCase(), code);
      trackEmailCodeEntered();
      if (returning) {
        trackLogin('email');
      } else {
        trackSignUp('email', { newsletter_opt_in: subscribeRef.current });
      }
      onSuccess(customer);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Verification failed';
      const lower = msg.toLowerCase();
      if (lower.includes('too many')) trackEmailVerifyFailed('rate_limited');
      else if (lower.includes('expired')) trackEmailVerifyFailed('expired');
      else trackEmailVerifyFailed('invalid');
      setEmailError(msg);
    } finally {
      setEmailBusy(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const returning = hadPriorSession();
    try {
      const idToken = await msalSignIn();
      const c = await signInWithMicrosoft(idToken, {
        newsletterOptIn: subscribeRef.current,
        signupSource,
      });
      if (returning) {
        trackLogin('microsoft');
      } else {
        trackSignUp('microsoft', { newsletter_opt_in: subscribeRef.current });
      }
      onSuccess(c);
    } catch (e) {
      const code = (e as { errorCode?: string } | undefined)?.errorCode;
      if (code === 'user_cancelled') return;
      if (code === 'popup_window_error') {
        toast({
          title: 'Popup blocked',
          description:
            'Allow popups for capsulechichub.com to sign in with Microsoft.',
        });
        return;
      }
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAppleSignIn = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const returning = hadPriorSession();
    try {
      const idToken = await appleSignIn();
      const c = await signInWithApple(idToken, {
        newsletterOptIn: subscribeRef.current,
        signupSource,
      });
      if (returning) {
        trackLogin('apple');
      } else {
        trackSignUp('apple', { newsletter_opt_in: subscribeRef.current });
      }
      onSuccess(c);
    } catch (e) {
      // Dismissing Apple's sheet is not a failure — say nothing.
      if (e instanceof AppleSignInCancelledError) return;
      // A blocked popup does need to speak: the user never got a choice and
      // can't discover the fix. Same wording as the Microsoft handler above.
      if (e instanceof AppleSignInPopupBlockedError) {
        toast({
          title: 'Popup blocked',
          description:
            'Allow popups for capsulechichub.com to sign in with Apple.',
        });
        return;
      }
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  if (!GOOGLE_CUSTOMER_CLIENT_ID) {
    return (
      <p className="m-0 text-sm text-red-700">
        Google sign-in is not configured yet
        (VITE_GOOGLE_CUSTOMER_CLIENT_ID missing).
      </p>
    );
  }

  // Shared button class: every OAuth pill in this card uses the same
  // rounded-full + bordered + flex layout, with the logo in a fixed left
  // column at left-3 and the label centred. h-11 is the canonical control
  // height shared with the email input and Log in button below.
  const oauthButtonClass = cn(
    'relative flex h-11 w-full items-center justify-center rounded-full border border-light-taupe bg-white px-11 font-sans text-[14.5px] font-medium text-charcoal shadow-sm transition-all duration-200 hover:border-soft-blush hover:shadow-md',
    'disabled:cursor-not-allowed disabled:opacity-60',
  );
  const iconWrapClass =
    'absolute left-3 top-1/2 flex h-[18px] w-[18px] -translate-y-1/2 items-center justify-center';

  return (
    <div className="flex flex-col items-center gap-2.5">
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={busy || !googleReady}
        className={oauthButtonClass}
      >
        <span className={iconWrapClass}>
          <GoogleGlyph className="h-[18px] w-[18px]" />
        </span>
        <span>Continue with Google</span>
      </button>

      {MICROSOFT_CUSTOMER_CLIENT_ID && (
        <button
          type="button"
          onClick={handleMicrosoftSignIn}
          disabled={busy}
          className={oauthButtonClass}
        >
          <span className={iconWrapClass}>
            <IcMicrosoft />
          </span>
          <span>Continue with Microsoft</span>
        </button>
      )}

      {APPLE_CUSTOMER_CLIENT_ID && (
        <button
          type="button"
          onClick={handleAppleSignIn}
          disabled={busy}
          className={oauthButtonClass}
        >
          <span className={iconWrapClass}>
            <IcApple />
          </span>
          {/* "Continue with Apple" is one of the two labels Apple's HIG
              permits (the other being "Sign in with Apple"); it also keeps
              the three OAuth pills reading identically. */}
          <span>Continue with Apple</span>
        </button>
      )}

      {/* "or" divider with hairlines either side — visually separates OAuth
          from email so both look like equal-weight options rather than
          email being a secondary fallback. */}
      <div className="my-2 flex w-full items-center gap-3 text-[12.5px] uppercase tracking-[0.2em] text-charcoal/40">
        <span className="h-px flex-1 bg-light-taupe" />
        <span>or</span>
        <span className="h-px flex-1 bg-light-taupe" />
      </div>

      {emailMode === 'email' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleRequestEmailCode();
          }}
          className="flex w-full flex-col gap-2"
        >
          <label
            htmlFor="cch-signin-email"
            className="m-0 text-[12.5px] font-medium text-charcoal/80"
          >
            Email
          </label>
          <div className="relative">
            {/* Icon at left-3 to share the same column as the OAuth buttons
                above. */}
            <Mail
              size={18}
              strokeWidth={1.75}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40"
            />
            <input
              id="cch-signin-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              placeholder="you@example.com"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              disabled={emailBusy}
              className="h-11 w-full rounded-full border border-light-taupe bg-white pl-10 pr-5 text-[14.5px] text-charcoal placeholder:text-charcoal/40 focus:border-soft-blush focus:outline-none disabled:opacity-60"
            />
          </div>
          <button
            type="submit"
            disabled={emailBusy}
            className="mt-1 flex h-11 items-center justify-center gap-2 rounded-full bg-soft-blush px-[18px] text-[14.5px] font-medium text-white shadow-sm transition-all duration-200 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
          >
            {emailBusy ? 'Sending…' : 'Log in'}
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleVerifyEmailCode();
          }}
          className="flex w-full flex-col gap-2"
        >
          <p className="m-0 text-center text-[13px] leading-snug text-charcoal/70">
            Code sent to <strong className="text-charcoal">{emailValue}</strong>.
            <button
              type="button"
              onClick={() => {
                setEmailMode('email');
                setCodeValue('');
                setEmailError(null);
              }}
              className="ml-1 text-soft-blush underline underline-offset-2"
            >
              Change
            </button>
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            autoFocus
            required
            placeholder="123456"
            value={codeValue}
            onChange={(e) =>
              setCodeValue(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            disabled={emailBusy}
            className="h-11 rounded-full border border-light-taupe bg-white px-5 text-center text-[18px] tracking-[0.4em] text-charcoal placeholder:text-charcoal/30 focus:border-soft-blush focus:outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={emailBusy || codeValue.length !== 6}
            className="flex h-11 items-center justify-center gap-2 rounded-full bg-soft-blush px-[18px] text-[14.5px] font-medium text-white shadow-sm transition-all duration-200 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
          >
            {emailBusy ? 'Verifying…' : 'Sign in'}
          </button>
          <button
            type="button"
            onClick={() => void handleRequestEmailCode(true)}
            disabled={emailBusy || resendCooldown > 0}
            className="m-0 mt-1 text-center text-[12.5px] text-charcoal/60 underline underline-offset-2 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
          >
            {resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : 'Resend code'}
          </button>
        </form>
      )}

      {emailError && (
        <p className="m-0 w-full text-center text-[13px] text-red-700">
          {emailError}
        </p>
      )}

      <label className="mt-4 flex w-full cursor-pointer items-start gap-2.5 text-[13.5px] leading-snug text-charcoal">
        <input
          type="checkbox"
          checked={subscribeToNewsletter}
          onChange={(e) => setSubscribeToNewsletter(e.target.checked)}
          className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer accent-soft-blush"
        />
        <span>Join my list for fresh picks and new edits</span>
      </label>

      {busy && <p className="mt-2 text-sm text-charcoal/60">Signing in…</p>}
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}

      <p className="mt-3 w-full text-center text-[11.5px] leading-[1.55] text-charcoal/50">
        By continuing you agree to the{' '}
        <a
          href="/terms"
          className="text-soft-blush underline underline-offset-2"
        >
          terms
        </a>{' '}
        and{' '}
        <a
          href="/privacy"
          className="text-soft-blush underline underline-offset-2"
        >
          privacy notice
        </a>
        .
      </p>
    </div>
  );
}
